'use client';
import { jsx as _jsx } from "react/jsx-runtime";
import { getHomeComputerProjectTarget } from '#pplx/frontend/apps/ask/ask-composer/getHomeComputerProjectTarget.js';
import { HomeComputerComposerChin } from '#pplx/frontend/apps/ask/ask-composer/HomeComputerComposerChin.js';
import { useAskInputSearchModes } from '#pplx/frontend/apps/ask/ask-input/ask-input-attributions/hooks/useAskInputSearchModes.js';
import AskInput from '#pplx/frontend/apps/ask/ask-input/ask-input-core/components/AskInput.js';
import { useAskInput } from '#pplx/frontend/apps/ask/ask-input/ask-input-hooks/useAskInput.js';
import { useAskInputState } from '#pplx/frontend/apps/ask/ask-input/ask-input-provider/components/AskInputProviderShared.js';
import { useAskDeepResearchModelSelectorOverrides } from '#pplx/frontend/apps/ask/ask-input/effort/useAskDeepResearchModelSelectorOverrides.js';
import { ComputerFileUploadUpsellBoundary } from '#pplx/frontend/apps/ask/computer-file-upload-upsell/ComputerFileUploadUpsellBoundary.js';
import { useHappyDispatch } from '#pplx/frontend/apps/ask/computer/branding/ComputerAnimatedLockupShared.js';
import { useComputerRemoteBoxExecutionTargetAddon } from '#pplx/frontend/apps/ask/computer/remotebox-execution-target/useComputerRemoteBoxExecutionTargetAddon.js';
import { homepageSearchModeSelectedEvent } from '#pplx/frontend/apps/ask/events/comet-home/analytics.js';
import { AskDeepResearchModeToggle } from '#pplx/frontend/apps/ask/hooks/ask-deep-research-hooks/AskDeepResearchModeToggle.js';
import { isAskDeepResearchInput } from '#pplx/frontend/apps/ask/hooks/ask-deep-research-hooks/deepResearchInput.js';
import { useComputerModeDecorativeGlow } from '#pplx/frontend/apps/ask/hooks/computer-hooks/useComputerModeDecorativeGlow.js';
import { useQueryFillParams } from '#pplx/frontend/apps/ask/hooks/query-fill-params-hooks/useQueryFillParams.js';
import { useSelectedSearchMode } from '#pplx/frontend/apps/ask/hooks/selected-search-mode-hooks/useSelectedSearchMode.js';
import { useComputerCommandMenuMentionTypeahead } from '#pplx/frontend/apps/ask/mention-typeahead/useMentionTypeaheadOptions.js';
import { useHomeAskInputAutofocus } from '#pplx/frontend/apps/ask/page-components/home/internal/useHomeAskInputAutofocus.js';
import { useHomeModeShortcuts } from '#pplx/frontend/apps/ask/page-components/home/useHomeModeShortcuts.js';
import { usePreloadThreadPagesOnIntent } from '#pplx/frontend/apps/ask/page-components/thread-pages-preload/usePreloadThreadPagesOnIntent.js';
import { useThreadConfig } from '#pplx/frontend/apps/ask/providers/ThreadSettingsProviderShared.js';
import { useHoverPlaceholder } from '#pplx/frontend/apps/ask/stores/queryInput.js';
import { useLocalUserSettings } from '#pplx/frontend/apps/ask/user-settings/getLocalUserSettings.js';
import { SearchMode } from '#pplx/frontend/ask-models/types/search-modes.js';
import { useIsSubscribed } from '#pplx/frontend/auth/useIsSubscribed.js';
import { useInstrumentation } from '#pplx/frontend/framework/instrumentation/useInstrumentation.js';
import { useIsMobile } from '#pplx/frontend/platform-components/useMobileStyle.js';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
const EMPTY_ATTACHMENTS = [];
/**
 * `LegacyHomeInput` component that is used to render the `AskInput` on the home page.
 * Extracted into its own wrapper so that the `useAskInput` hook provider
 * triggers a render for as few components as necessary, instead of the entire
 * `InnerHome` component.
 * @returns AskInput
 */
export const LegacyHomeInput = React.memo(function LegacyHomeInput({ ref, recessedFrame = false }) {
    const reason = 'home-ask-input';
    const { attachments = EMPTY_ATTACHMENTS } = useAskInputState();
    const selectedSearchMode = useSelectedSearchMode();
    const preloadThreadPagesOnIntent = usePreloadThreadPagesOnIntent(selectedSearchMode);
    const [selectedProject, setSelectedProject] = useLocalUserSettings('homeComputerProjectSelection', null);
    const isComputerInputFooterAvailable = !import.meta.env.VITE_IS_SIDECAR_BUILD;
    const previousSearchModeRef = useRef(selectedSearchMode);
    // Restored settings and drafts can resolve after paint. Only a mode-selection
    // action should enable chin motion, including the legacy rail's first measurement.
    const [animateChin, setAnimateChin] = useState(false);
    const { askDeepResearchModel } = useThreadConfig();
    const localAskInputRef = useRef(null);
    // Reuse the caller's object ref (mobile overlay) when provided so we don't
    // need a ref-merging helper, keeping the home chunk under its size limit.
    const askInputRef = ref ?? localAskInputRef;
    const autofocusRef = useMemo(() => ({
        current: {
            getFocusState: () => askInputRef.current?.getFocusState() ?? null,
            focus: () => askInputRef.current?.focusInput(),
        },
    }), [askInputRef]);
    const { homeAutofocusReady, initialConfiguredModelResolved } = useHomeAskInputAutofocus(autofocusRef);
    const [, setLastSubmittedAskInputMode] = useLocalUserSettings('lastSubmittedAskInputMode', undefined);
    const handleAcceptedSubmit = useCallback(() => {
        setLastSubmittedAskInputMode(selectedSearchMode === SearchMode.ASI && !askDeepResearchModel ? 'computer' : 'search');
    }, [selectedSearchMode, askDeepResearchModel, setLastSubmittedAskInputMode]);
    const { collection, targetThreadAccessLevel } = getHomeComputerProjectTarget({
        selectedProject: isComputerInputFooterAvailable ? selectedProject : null,
        selectedSearchMode,
    });
    const isComputer = selectedSearchMode === SearchMode.ASI;
    const isComputerChinVisible = isComputerInputFooterAvailable && isComputer;
    const { addon: remoteBoxAddon, workflowConfig: remoteBoxWorkflowConfig } = useComputerRemoteBoxExecutionTargetAddon({
        eligible: isComputer && isComputerInputFooterAvailable,
        mode: { mode: 'new-task', projectUuid: collection?.uuid },
    });
    const [value, json, actions] = useAskInput({
        collection,
        workflowConfig: remoteBoxWorkflowConfig,
        csrRedirect: true,
        onAcceptedSubmit: handleAcceptedSubmit,
        reason,
        targetThreadAccessLevel,
    });
    const decorativeGlow = useComputerModeDecorativeGlow(selectedSearchMode);
    const deepResearchModelSelectorOverrides = useAskDeepResearchModelSelectorOverrides({
        enabled: isAskDeepResearchInput({ askDeepResearchModel, searchMode: selectedSearchMode, json }),
    });
    useHappyDispatch(selectedSearchMode === SearchMode.ASI, value);
    useQueryFillParams({ setUserInput: actions.setUserInput });
    const { $t } = useIntl();
    const { track } = useInstrumentation();
    const { isMobileStyle, isMobileUserAgent } = useIsMobile();
    const inputAtBottom = !!import.meta.env.VITE_IS_SIDECAR_BUILD || (isMobileStyle && !isMobileUserAgent);
    const autosuggestionsEnabled = !import.meta.env.VITE_IS_SIDECAR_BUILD && selectedSearchMode !== SearchMode.ASI;
    const { mentionTypeaheadOptions, mentionIconRenderer, onTriggerTypeahead, onMentionSearchQueryChange, computerCommandMenuOpen, onComputerCommandMenuOpenChange, } = useComputerCommandMenuMentionTypeahead({
        reason,
        isLazy: true,
        omitDeviceMentions: remoteBoxAddon !== null,
    });
    const hoverPlaceholder = useHoverPlaceholder();
    const computerPlaceholder = selectedSearchMode === SearchMode.ASI
        ? $t({
            id: 'aa0072cd64',
            defaultMessage: 'Do anything…',
            description: 'Placeholder text for the homepage computer ask input field',
        })
        : undefined;
    const placeholder = useMemo(() => {
        if (hoverPlaceholder) {
            return hoverPlaceholder;
        }
        return computerPlaceholder ? [computerPlaceholder] : undefined;
    }, [computerPlaceholder, hoverPlaceholder]);
    const handleFocusInput = useCallback(() => askInputRef.current?.focusInput(), [askInputRef]);
    const bottomComponent = useCallback(({ onInsertMentionNode, onRemoveMentionNode }) => {
        if (!isComputerInputFooterAvailable)
            return undefined;
        const rail = (_jsx(HomeComputerComposerChin, { framed: recessedFrame, isVisible: isComputerChinVisible, animate: animateChin, onFocusInput: handleFocusInput, onInsertMentionNode: onInsertMentionNode, onRemoveMentionNode: onRemoveMentionNode, selectedProject: selectedProject, onSelectedProjectChange: setSelectedProject, renderChin: remoteBoxAddon === null ? undefined : () => remoteBoxAddon, showFreePreviewLabel: true }));
        return rail;
    }, [
        animateChin,
        handleFocusInput,
        isComputerInputFooterAvailable,
        remoteBoxAddon,
        recessedFrame,
        selectedProject,
        isComputerChinVisible,
        setSelectedProject,
    ]);
    useEffect(() => {
        previousSearchModeRef.current = selectedSearchMode;
    }, [selectedSearchMode]);
    const handleSearchModeSelected = useCallback((searchMode) => {
        const previousMode = previousSearchModeRef.current;
        if (previousMode === searchMode || import.meta.env.VITE_IS_SIDECAR_BUILD) {
            return;
        }
        setAnimateChin(true);
        track(homepageSearchModeSelectedEvent({
            name: 'homepage search mode selected',
            data: {
                previousMode,
                selectedMode: searchMode,
            },
        }));
    }, [track]);
    const { searchModeControllerProps: homeSearchModeControllerProps } = useAskInputSearchModes({
        enabled: initialConfiguredModelResolved,
        onSearchModeSelected: handleSearchModeSelected,
    });
    const { subscriptionTier } = useIsSubscribed();
    useHomeModeShortcuts({
        enabled: homeSearchModeControllerProps.showSearchMode === true,
        searchEnabled: subscriptionTier !== 'basic' && subscriptionTier !== 'viewer',
        onSearchModeSelect: homeSearchModeControllerProps.onSearchModeSelect,
    });
    const renderComputerModeToggle = useCallback(({ toggle, onSelectComputer, searchMode, }) => (_jsx(AskDeepResearchModeToggle, { children: _jsx(ComputerFileUploadUpsellBoundary, { attachments: attachments, onUseComputer: onSelectComputer, searchMode: searchMode, surface: "homepage", children: toggle }, "computer-file-upload-mode-toggle") })), [attachments]);
    return (_jsx(AskInput, { ...actions, ref: askInputRef, value: value, json: json, placeholder: placeholder, placeholderAnimation: hoverPlaceholder ? 'fade' : undefined, placeholderClassName: "line-clamp-2 overflow-hidden max-h-[3em]", querySource: "home", modelSelectorOverrides: deepResearchModelSelectorOverrides, showSkills: true, autofocus: homeAutofocusReady, disableSubmission: !initialConfiguredModelResolved, initialLayout: import.meta.env.VITE_IS_SIDECAR_BUILD ? 'compact' : 'expanded', minRows: import.meta.env.VITE_IS_SIDECAR_BUILD ? 1 : 2, maxRows: 10, hasShadow: true, appearance: recessedFrame ? (isComputer ? 'framed-accent' : 'framed') : 'raised', framedChinVisible: recessedFrame && isComputerChinVisible, decorativeGlow: recessedFrame ? undefined : decorativeGlow, showFileUpload: true, dropBehavior: "file-viewport-wide", renderComputerModeToggle: renderComputerModeToggle, showIncognitoHint: true, showSources: initialConfiguredModelResolved && !import.meta.env.VITE_IS_SIDECAR_BUILD, autosuggestionsEnabled: autosuggestionsEnabled, computerCommandMenuOpen: computerCommandMenuOpen, onComputerCommandMenuOpenChange: onComputerCommandMenuOpenChange, onSearchModeSelected: handleSearchModeSelected, onSearchModeCleared: handleSearchModeSelected, onClick: preloadThreadPagesOnIntent.onClick, onKeyDown: preloadThreadPagesOnIntent.onKeyDown, dropdownPlacement: inputAtBottom ? 'top' : 'bottom', bottomComponent: bottomComponent, mentionTypeaheadOptions: mentionTypeaheadOptions, mentionIconRenderer: mentionIconRenderer, onTriggerTypeahead: onTriggerTypeahead, onMentionSearchQueryChange: onMentionSearchQueryChange, layoutKey: "home-ask-input" }));
});
//# sourceMappingURL=LegacyHomeInput.js.map